import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { execute } from '@/graphql/execute';
import { createProjectLinkMutation } from '@/mutations/project.mutation';
import { useDialog } from '@/providers/DialogProvider';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const createLinkSchema = z
  .object({
    expiresAt: z.string().min(1, 'Expiration date is required'),
    name: z.string().min(1, 'Name is required'),
    password: z.string().optional(),
  })
  .refine(
    (data) => {
      const expirationDate = dayjs(data.expiresAt);
      const now = dayjs();
      const maxDate = now.add(3, 'months');

      if (expirationDate.isBefore(now)) {
        return false;
      }
      if (expirationDate.isAfter(maxDate)) {
        return false;
      }
      return true;
    },
    {
      message: 'Expiration date must be between now and 3 months from now',
      path: ['expiresAt'],
    },
  );

type CreateLinkFormData = z.infer<typeof createLinkSchema>;

interface ProjectLinkCreateDialogProps {
  projectId: string;
}

export function ProjectLinkCreateDialog({ projectId }: ProjectLinkCreateDialogProps) {
  const { dismissActiveDialog } = useDialog();
  const [isCreating, setIsCreating] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<CreateLinkFormData>({
    resolver: zodResolver(createLinkSchema),
    defaultValues: {
      expiresAt: dayjs().add(1, 'month').format('YYYY-MM-DD'),
      name: 'Untitled',
      password: '',
    },
  });

  // Create project link mutation
  const createLinkMutation = useMutation({
    mutationFn: async (data: CreateLinkFormData) => {
      return execute(createProjectLinkMutation, {
        projectId,
        expiresAt: dayjs(data.expiresAt).toDate(),
        name: data.name,
        password: data.password || null,
      });
    },
    onSuccess: () => {
      toast.success('Project link created successfully');
      form.reset();
      dismissActiveDialog();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create project link');
    },
  });

  const handleCreateLink = async (data: CreateLinkFormData) => {
    setIsCreating(true);
    try {
      await createLinkMutation.mutateAsync(data);
      queryClient.invalidateQueries({ queryKey: ['projectLinks', projectId] });
    } finally {
      setIsCreating(false);
    }
  };

  const getMinDate = () => {
    return dayjs().format('YYYY-MM-DD');
  };

  const getMaxDate = () => {
    return dayjs().add(3, 'months').format('YYYY-MM-DD');
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Create Share Link</DialogTitle>
        <DialogDescription>
          Create a new shareable link for this project. The link can be set to expire up to 3 months from now.
        </DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleCreateLink)} className='space-y-6'>
          <FormField
            control={form.control}
            name='name'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isCreating} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='expiresAt'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Expiration Date</FormLabel>
                <FormControl>
                  <Input type='date' min={getMinDate()} max={getMaxDate()} {...field} disabled={isCreating} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='password'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password (optional)</FormLabel>
                <FormControl>
                  <Input type='password' placeholder='Leave empty for no password' {...field} disabled={isCreating} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <DialogFooter>
            <Button type='button' variant='outline' onClick={dismissActiveDialog} disabled={isCreating}>
              Cancel
            </Button>
            <Button type='submit' disabled={isCreating}>
              {isCreating ? 'Creating...' : 'Create Link'}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
